import React, { Component } from 'react';
import './Collection.css';
import '@shopify/polaris/dist/styles.css';
import { RangeSlider, Select, Page, Card, TextStyle, TextField, FormLayout, DisplayText, Button, Checkbox, Banner, Layout, Spinner, Stack } from '@shopify/polaris';
import { postFetch, putFetch, getFetch } from '../helpers/request';
import { Redirect } from 'react-router';
import AutocompleteTags from './AutocompleteTags';
import TagsInput from './TagsInput';
import TitleRules, { TitleRule, invalidTitleRules, serializeTitleRules } from './TitleRules';
import collectionImage from '../images/advcol1.svg';
import selectionListImage from '../images/advcol2.svg';

const collectionSelectionHelp = 'Choose from manual and automated Shopify collections, including those managed by Advanced Collections. A collection cannot select itself. Type at least 2 letters to search.';

const canonicalCollectionId = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
  const id = String(value);
  if (!/^[0-9]+$/.test(id)) return null;
  const canonical = id.replace(/^0+(?=\d)/, '');
  return canonical === '0' ? null : canonical;
};

interface CollectionProps {
  type: string,
  shopOrigin: string,
  id?: string
}

interface CollectionState {
  smartCollectionOptions: Array<any>,
  availableTags: any,
  includeSelectedTags: any,
  excludeSelectedTags: any,
  filterSelectedTags: any,
  typeOptions:any,
  includeTypes: any,
  excludeTypes: any,
  filterTypes: any,
  vendorOptions: any,
  includeVendors: any,
  excludeVendors: any,
  filterVendors: any,
  hasLoaded: boolean,
  errorLoading: boolean,
  collectionTitle: string,
  isSaving: boolean,
  autoSync: boolean,
  collectionTitleTouched: boolean,
  redirectToListing: boolean,
  showBanner:boolean,
  selectedSmartCollectionIds: Array<any>,
  excludedSmartCollectionIds: Array<any>,
  filteredSmartCollectionIds: Array<any>,
  includeTitleRules: TitleRule[],
  excludeTitleRules: TitleRule[],
  filterTitleRules: TitleRule[],
  saveError: string,
  includeStartsWithTags: any[],
  includeEndsWithTags: any[],
  includeContainsTags: any[],
  excludeStartsWithTags: any[],
  excludeEndsWithTags: any[],
  excludeContainsTags: any[],
  includePriceLowerThanComparedAt: boolean,
  includeDiscountType:string,
  excludePriceLowerThanComparedAt: boolean,
  excludeDiscountType:string,
  filterPriceLowerThanComparedAt: boolean,
  filterDiscountType:string,
  includeAllProducts: boolean,
  includeNewnessType: string,
  includeNewnessDays: string,
  excludeNewnessType: string,
  excludeNewnessDays: string,
  includeDiscountPercentageRange: any,
  excludeDiscountPercentageRange: any,
  filterDiscountPercentageRange: any,
  includeInventoryType: string,
  includeInventoryQuantity: string,
  excludeInventoryType: string,
  excludeInventoryQuantity: string,
  excludeInventoryIgnoreContinue: boolean,
  excludeInventoryIgnoreUntracked: boolean,
  exceedProductsCount: boolean
}

class Collection extends Component <CollectionProps, CollectionState> {

  constructor(props: CollectionProps) {
    super(props);
    this.state = { 
      isSaving: false,
      hasLoaded: false,
      errorLoading: false,
      collectionTitle: '',
      autoSync: true,
      collectionTitleTouched: false,
      redirectToListing: false,
      availableTags: [],
      typeOptions: [],
      includeTypes: [],
      excludeTypes: [],
      filterTypes: [],
      vendorOptions: [],
      includeVendors: [],
      excludeVendors: [],
      filterVendors: [],
      includeSelectedTags: [],
      excludeSelectedTags: [],
      filterSelectedTags: [],
      smartCollectionOptions: [],
      showBanner: true,
      selectedSmartCollectionIds: [],
      excludedSmartCollectionIds: [],
      filteredSmartCollectionIds: [],
      includeTitleRules: [],
      excludeTitleRules: [],
      filterTitleRules: [],
      saveError: '',
      includeStartsWithTags: [],
      includeEndsWithTags: [],
      includeContainsTags: [],
      excludeStartsWithTags: [],
      excludeEndsWithTags: [],
      excludeContainsTags: [],
      includePriceLowerThanComparedAt: false,
      includeDiscountType: 'none',
      excludePriceLowerThanComparedAt: false,
      excludeDiscountType: 'none',
      filterPriceLowerThanComparedAt: false,
      filterDiscountType: 'none',
      includeAllProducts: false,
      includeNewnessType: 'none',
      includeNewnessDays: '30',
      excludeNewnessType: 'none',
      excludeNewnessDays: '30',
      includeDiscountPercentageRange: [0, 100],
      excludeDiscountPercentageRange: [0, 100],
      filterDiscountPercentageRange: [0, 100],
      includeInventoryType: 'none',
      includeInventoryQuantity: '0',
      excludeInventoryType: 'none',
      excludeInventoryQuantity: '0',
      excludeInventoryIgnoreContinue: false,
      excludeInventoryIgnoreUntracked: false,
      exceedProductsCount: false
    };
  }
  

  componentDidUpdate(prevProps: CollectionProps, prevState: CollectionState) {
    if (!prevState.hasLoaded && this.state.hasLoaded) {
      (window as any).shopify.loading(false);
    }
  }

  componentWillUnmount() {
    (window as any).shopify.loading(false);
  }

  componentDidMount() {
    (window as any).shopify.loading(true);
    if (this.props.type === 'new') {
      this.loadTags();
    } else if (this.props.type === 'existing') {
      getFetch('/app/api/collections/?id='+this.props.id, {})
        .then((res:any) => res.json()
        ).then(data => {
          this.setState({collectionTitle: data.collection_title});
          this.setState({includeSelectedTags: data.include_tags});
          this.setState({excludeSelectedTags: data.exclude_tags});
          this.setState({filterSelectedTags: data.filter_tags})
          this.setState({selectedSmartCollectionIds: data.associated_smart_collections});
          this.setState({excludedSmartCollectionIds: data.excluded_smart_collections});
          this.setState({
            filteredSmartCollectionIds: data.filtered_smart_collections || [],
            includeTitleRules: data.include_title_rules || [],
            excludeTitleRules: data.exclude_title_rules || [],
            filterTitleRules: data.filter_title_rules || [],
          });
          this.setState({autoSync: data.auto_sync});
          
          if (data.include_vendors) {
            this.setState({includeVendors: data.include_vendors.split('/&/')});
          }

          if (data.exclude_vendors) {
            this.setState({excludeVendors: data.exclude_vendors.split('/&/')});
          }

          if (data.filter_vendors) {
            this.setState({filterVendors: data.filter_vendors.split('/&/')});
          }

          if (data.include_types) {
            this.setState({includeTypes: data.include_types.split('/&/')});
          }

          if (data.exclude_types) {
            this.setState({excludeTypes: data.exclude_types.split('/&/')});
          }

          if (data.filter_types) {
            this.setState({filterTypes: data.filter_types.split('/&/')});
          }

          if (data.include_tags_starts_with) {
            this.setState({includeStartsWithTags: data.include_tags_starts_with.split(',')});
          }

          if (data.include_tags_ends_with) {
            this.setState({includeEndsWithTags: data.include_tags_ends_with.split(',')});
          }
          
          if (data.include_tags_contains) {
            this.setState({includeContainsTags: data.include_tags_contains.split(',')});
          }
          if (data.exclude_tags_starts_with) {
            this.setState({excludeStartsWithTags: data.exclude_tags_starts_with.split(',')});
          }
          if (data.exclude_tags_ends_with) {
            this.setState({excludeEndsWithTags: data.exclude_tags_ends_with.split(',')});
          }
          if (data.exclude_tags_contains) {
            this.setState({excludeContainsTags: data.exclude_tags_contains.split(',')});
          }

          this.setState({includePriceLowerThanComparedAt: data.include_price_less_than_compare_at});
          this.setState({includeDiscountType: data.include_discount_type});
          this.setState({includeDiscountPercentageRange: [data.include_discount_min ? data.include_discount_min : 0, 
            data.include_discount_max ? data.include_discount_max : 100]});

          this.setState({excludePriceLowerThanComparedAt: data.exclude_price_less_than_compare_at});
          this.setState({excludeDiscountType: data.exclude_discount_type});
          this.setState({excludeDiscountPercentageRange: [data.exclude_discount_min ? data.exclude_discount_min : 0, 
            data.exclude_discount_max ? data.exclude_discount_max : 100]});
          
          this.setState({filterPriceLowerThanComparedAt: data.filter_price_less_than_compare_at});
          this.setState({filterDiscountType: data.filter_discount_type});
          this.setState({filterDiscountPercentageRange: [data.filter_discount_min ? data.filter_discount_min : 0, 
            data.filter_discount_max ? data.filter_discount_max : 100]});

          this.setState({includeNewnessType: data.include_newness_type});
          this.setState({includeNewnessDays: '' + data.include_newness_days});
          this.setState({excludeNewnessType: data.exclude_newness_type});
          this.setState({excludeNewnessDays: '' + data.exclude_newness_days});

          this.setState({includeInventoryType: data.include_inventory_type});
          this.setState({includeInventoryQuantity: '' + data.include_inventory_quantity});
          this.setState({excludeInventoryType: data.exclude_inventory_type});
          this.setState({excludeInventoryQuantity: '' + data.exclude_inventory_quantity});
          this.setState({excludeInventoryIgnoreContinue: data.exclude_inventory_ignore_continue});
          this.setState({excludeInventoryIgnoreUntracked: data.exclude_inventory_ignore_untracked});

          this.setState({includeAllProducts: data.include_all_products});

          this.loadTags();
        });
    }
  }

  loadTags = () => {
    // Older cached lists omit app-managed collections. Fetch the complete list
    // once with the new key, then exclude only this editor's target at render.
    const tagsKey = this.selectionCacheKey();
    let supportsLocalStorage = true;
    let data = null;
    try {
      data = localStorage.getItem(tagsKey);
    } catch (e) {
      supportsLocalStorage = false;
    }
    if (data === null || !supportsLocalStorage) {
      getFetch('/app/api/shopify/products-tags', {})
        .then((res:any) => res.json()
        ).then(data => {
          if (supportsLocalStorage) {
            localStorage.setItem(tagsKey, JSON.stringify(data));
          }
          this.initTags(data);
        }).catch(err => {
          this.setState({ hasLoaded: true });
          this.setState({ errorLoading: true });
          console.log(err);
      });
    } else {
      this.initTags(JSON.parse(localStorage.getItem(tagsKey)!));
    }
  }

  selectionCacheKey = () => 'adv-col/v2/' + this.props.shopOrigin;

  refreshPage = () => {
    let supportsLocalStorage = true;
    try {
      localStorage.getItem('test');
    } catch (e) {
      supportsLocalStorage = false;
    }
    if (supportsLocalStorage) {
      localStorage.removeItem(this.selectionCacheKey());
    }
    window.location.reload(false);
  }

  renderSelectionHeading(title: string, refreshLabel: string) {
    return (
      <Stack alignment="center" spacing="tight">
        <TextStyle variation="strong">{title}</TextStyle>
        <Button plain onClick={this.refreshPage}>{refreshLabel}</Button>
      </Stack>
    );
  }

  hasInvalidNewRules = () =>
    [this.state.includeTitleRules, this.state.excludeTitleRules, this.state.filterTitleRules]
      .some(invalidTitleRules) || this.state.filteredSmartCollectionIds.length > 100;

  save = async () => {
    if (this.state.isSaving) return;
    if (this.hasInvalidNewRules()) {
      this.setState({saveError: 'Check the title conditions and collection filters below before saving.'});
      return;
    }
    this.setState({isSaving:true, saveError: ''});
    
    let body:any = {
      collection_title: this.state.collectionTitle,
      include_tags: this.state.includeSelectedTags,
      exclude_tags: this.state.excludeSelectedTags,
      filter_tags: this.state.filterSelectedTags,
      include_tags_starts_with: this.state.includeStartsWithTags.join(','),
      include_tags_ends_with: this.state.includeEndsWithTags.join(','),
      include_tags_contains: this.state.includeContainsTags.join(','),
      exclude_tags_starts_with: this.state.excludeStartsWithTags.join(','),
      exclude_tags_ends_with: this.state.excludeEndsWithTags.join(','),
      exclude_tags_contains: this.state.excludeContainsTags.join(','),
      include_price_less_than_compare_at: this.state.includePriceLowerThanComparedAt,
      include_discount_type: this.state.includeDiscountType,
      include_discount_min: this.state.includeDiscountPercentageRange[0],
      include_discount_max: this.state.includeDiscountPercentageRange[1],
      exclude_price_less_than_compare_at: this.state.excludePriceLowerThanComparedAt,
      exclude_discount_type: this.state.excludeDiscountType,
      exclude_discount_min: this.state.excludeDiscountPercentageRange[0],
      exclude_discount_max: this.state.excludeDiscountPercentageRange[1],
      filter_price_less_than_compare_at: this.state.filterPriceLowerThanComparedAt,
      filter_discount_type: this.state.filterDiscountType,
      filter_discount_min: this.state.filterDiscountPercentageRange[0],
      filter_discount_max: this.state.filterDiscountPercentageRange[1],
      include_all_products: this.state.includeAllProducts,
      include_newness_type: this.state.includeNewnessType,
      include_newness_days: this.state.includeNewnessDays,
      exclude_newness_type: this.state.excludeNewnessType,
      exclude_newness_days: this.state.excludeNewnessDays,
      include_inventory_type: this.state.includeInventoryType,
      include_inventory_quantity: this.state.includeInventoryQuantity,
      exclude_inventory_type: this.state.excludeInventoryType,
      exclude_inventory_quantity: this.state.excludeInventoryQuantity,
      exclude_inventory_ignore_continue: this.state.excludeInventoryIgnoreContinue,
      exclude_inventory_ignore_untracked: this.state.excludeInventoryIgnoreUntracked,
      auto_sync: this.state.autoSync,
      associated_smart_collections: this.state.selectedSmartCollectionIds,
      excluded_smart_collections: this.state.excludedSmartCollectionIds,
      filtered_smart_collections: this.state.filteredSmartCollectionIds,
      include_title_rules: serializeTitleRules(this.state.includeTitleRules),
      exclude_title_rules: serializeTitleRules(this.state.excludeTitleRules),
      filter_title_rules: serializeTitleRules(this.state.filterTitleRules),
      include_types:this.state.includeTypes.join('/&/'),
      exclude_types:this.state.excludeTypes.join('/&/'),
      filter_types:this.state.filterTypes.join('/&/'),
      include_vendors: this.state.includeVendors.join('/&/'),
      exclude_vendors: this.state.excludeVendors.join('/&/'),
      filter_vendors: this.state.filterVendors.join('/&/'),
    };

    try {
      if (this.props.type === 'existing') body.id = this.props.id;
      const response = this.props.type === 'new'
        ? await postFetch('/app/api/collections/create/', body)
        : await putFetch('/app/api/collections/update/', body);
      if (!response || !response.ok || response.redirected) {
        let saveError = 'Unable to save the collection. Check your conditions and try again.';
        if (response && response.status === 400) {
          try {
            const errors = await response.json();
            const messages = ['include_title_rules', 'exclude_title_rules', 'filter_title_rules',
              'associated_smart_collections', 'excluded_smart_collections', 'filtered_smart_collections']
              .reduce<string[]>((all, key) => all.concat(Array.isArray(errors[key])
                ? errors[key].filter((value: unknown) => typeof value === 'string') : []), []);
            if (messages.length) saveError = messages.join(' ');
          } catch (error) { /* Retain the retry message if the error body is unavailable. */ }
        }
        this.setState({isSaving: false, saveError});
        return;
      }
      this.setState({redirectToListing: true});
    } catch (error) {
      this.setState({isSaving: false, saveError: 'Unable to save the collection. Please try again.'});
    }
  }
  
  handleTitleChange = (value:string) => {
    this.setState({collectionTitleTouched: true});
    this.setState({collectionTitle: value});
  };

  handleIncludeStartsWithTagsChange = (includeStartsWithTags: Array<string>) => {
    this.setState({includeStartsWithTags: includeStartsWithTags});
  }

  handleIncludeEndsWithTagsChange = (includeEndsWithTags: Array<string>) => {
    this.setState({includeEndsWithTags: includeEndsWithTags});
  } 

  handleIncludeContainsTagsChange = (includeContainsTags: Array<string>) => {
    this.setState({includeContainsTags: includeContainsTags});
  }

  handleExcludeStartsWithTagsChange = (excludeStartsWithTags: Array<string>) => {
    this.setState({excludeStartsWithTags: excludeStartsWithTags});
  }

  handleExcludeEndsWithTagsChange = (excludeEndsWithTags: Array<string>) => {
    this.setState({excludeEndsWithTags: excludeEndsWithTags});
  } 

  handleExcludeContainsTagsChange = (excludeContainsTags: Array<string>) => {
    this.setState({excludeContainsTags: excludeContainsTags});
  } 

  handleIncludePriceLowerThanComparedAtChange = (value:boolean) => {
    this.setState({includePriceLowerThanComparedAt: value});
  };

  handleIncludeDiscountTypeChange =(value:string) => {
    this.setState({includeDiscountType: value});
  }

  handleIncludeDiscountPercentageRangeChange = (value:any) => {
    this.setState({includeDiscountPercentageRange: value});
  }

  handleExcludeDiscountTypeChange =(value:string) => {
    this.setState({excludeDiscountType: value});
  }

  handleExcludePriceLowerThanComparedAtChange = (value:boolean) => {
    this.setState({excludePriceLowerThanComparedAt: value});
  };

  handleExcludeDiscountPercentageRangeChange = (value:any) => {
    this.setState({excludeDiscountPercentageRange: value});
  }

  handleFilterDiscountTypeChange =(value:string) => {
    this.setState({filterDiscountType: value});
  }

  handleFilterPriceLowerThanComparedAtChange = (value:boolean) => {
    this.setState({filterPriceLowerThanComparedAt: value});
  };

  handleFilterDiscountPercentageRangeChange = (value:any) => {
    this.setState({filterDiscountPercentageRange: value});
  }

  handleIncludeAllProducts = (value:boolean) => {
    this.setState({includeAllProducts: value});
  };

  handleAutoSyncChange = (value:boolean) => {
    this.setState({autoSync: value});
  };

  includeTagsChange = (selectedTags: Array<string>) => {
    this.setState({includeSelectedTags:selectedTags});
  }

  handleIncludeNewnessTypeChange = (value: string) => {
    this.setState({includeNewnessType: value});
  }
  
  handleIncludeNewnessDaysChange = (value: string) => {
    this.setState({includeNewnessDays: value});
  }

  handleExcludeNewnessTypeChange = (value: string) => {
    this.setState({excludeNewnessType: value});
  }

  handleExcludeNewnessDaysChange = (value: string) => {
    this.setState({excludeNewnessDays: value});
  }

  // vendors
  handleIncludeVendorsChange = (selectedVendors: Array<string>) => {
    this.setState({includeVendors: selectedVendors});
  }

  handleExcludeVendorsChange = (selectedVendors: Array<string>) => {
    this.setState({excludeVendors: selectedVendors});
  }

  handleFilterVendorsChange = (selectedVendors: Array<string>) => {
    this.setState({filterVendors: selectedVendors});
  }

  // types
  handleIncludeTypesChange = (selectedTypes: Array<string>) => {
    this.setState({includeTypes: selectedTypes});
  }

  handleExcludeTypesChange = (selectedTypes: Array<string>) => {
    this.setState({excludeTypes: selectedTypes});
  }

  handleFilterTypesChange = (selectedTypes: Array<string>) => {
    this.setState({filterTypes: selectedTypes});
  }

  // inventory
  handleIncludeInventoryTypeChange = (value: string) => {
    this.setState({includeInventoryType: value});
  }
  
  handleIncludeInventoryQuantityChange = (value: string) => {
    this.setState({includeInventoryQuantity: value});
  }

  handleExcludeInventoryTypeChange = (value: string) => {
    this.setState({excludeInventoryType: value});
  }

  handleExcludeInventoryQuantityChange = (value: string) => {
    this.setState({excludeInventoryQuantity: value});
  }

  handleExcludeInventoryIgnoreContinueChange = (value: boolean) => {
    this.setState({excludeInventoryIgnoreContinue: value});
  }

  handleExcludeInventoryIgnoreUntrackedChange = (value: boolean) => {
    this.setState({excludeInventoryIgnoreUntracked: value});
  }

  // tags
  excludeTagsChange = (selectedTags: Array<string>) => {
    this.setState({excludeSelectedTags:selectedTags});
  }

  filterTagsChange = (selectedTags: Array<string>) => {
    this.setState({filterSelectedTags:selectedTags});
  }

  selectedSmartCollectionChange = (selectedIds: Array<number>) => {
    this.setState({selectedSmartCollectionIds: selectedIds});
  }

  excludedSmartCollectionChange = (selectedIds: Array<number>) => {
    this.setState({excludedSmartCollectionIds: selectedIds});
  }

  filteredSmartCollectionChange = (selectedIds: Array<number>) => {
    this.setState({filteredSmartCollectionIds: selectedIds});
  }

  convertStringArrayToValueLabelObjectArray(ss: Array<string>) {
    let oa = [];
    for (let s of ss) {
      oa.push({
        value: s, label: s
      });
    }
    return oa;
  }

  initTags(data:any) {
    if (!data.exceed_products_count) {
      let availableTags = data.tags;
      availableTags.sort();
      let availableVendors = data.vendors;
      availableVendors.sort();
      let availableTypes = data.types;
      availableTypes.sort();
      this.setState({ typeOptions: this.convertStringArrayToValueLabelObjectArray(availableTypes) });
      this.setState({ vendorOptions: this.convertStringArrayToValueLabelObjectArray(availableVendors) });
      this.setState({ availableTags: this.convertStringArrayToValueLabelObjectArray(availableTags) });
      // updates available tags list
      this.includeTagsChange(this.state.includeSelectedTags);
      this.excludeTagsChange(this.state.excludeSelectedTags);
    }
    this.setState({ exceedProductsCount: data.exceed_products_count });
    let smartCollectionOptions = [];
    let smartCollections = data.smart_collections;
    for (let smartCollection of smartCollections) {
      smartCollectionOptions.push(
        {
          label: smartCollection.title,
          value: smartCollection.id
        }
      );
    }
    this.setState({ smartCollectionOptions: smartCollectionOptions});
    this.setState({ hasLoaded: true });
  }
  

  render() {
    const currentCollectionId = this.props.type === 'existing' ? canonicalCollectionId(this.props.id) : null;
    const selectableCollections = this.state.smartCollectionOptions.filter(option =>
      currentCollectionId === null || canonicalCollectionId(option.value) !== currentCollectionId);
    let inventoryTypeOptions = [
      {'label': 'No', 'value': 'none'},
      {'label': 'Total quantity equals to', 'value': 'total-equals'},
      {'label': 'Total quantity less than', 'value': 'total-less_than'},
      {'label': 'Total quantity more than', 'value': 'total-more_than'},
      {'label': 'Any variant quantity equals to', 'value': 'any-equals'},
      {'label': 'Any variant quantity less than', 'value': 'any-less_than'},
      {'label': 'Any variant quantity more than', 'value': 'any-more_than'},
      {'label': 'All variant quantity equals to', 'value': 'all-equals'},
      {'label': 'All variant quantity less than', 'value': 'all-less_than'},
      {'label': 'All variant quantity more than', 'value': 'all-more_than'},
      {'label': 'Main product / first variant quantity equals to', 'value': 'first-equals'},
      {'label': 'Main product / first variant quantity less than', 'value': 'first-less_than'},
      {'label': 'Main product / first variant quantity more than', 'value': 'first-more_than'},
    ];
    let newnessTypeOptions = [
      {'label': 'No', 'value': 'none'},
      {'label': 'Within days from Updated Date', 'value': 'updated_at-within'},
      {'label': 'Within days from Published Date', 'value': 'published_at-within'},
      {'label': 'Within days from Created Date', 'value': 'created_at-within'},
      {'label': 'More than days from Updated Date', 'value': 'updated_at-more_than'},
      {'label': 'More than days from Published Date', 'value': 'published_at-more_than'},
      {'label': 'More than days from Created Date', 'value': 'created_at-more_than'},
    ];
    let discountTypeOptions = [
      {'label': "As long as any variant price is lower than its compared at price", 'value': 'none'},
      {'label': "Only if all variants have their prices lower than their compared at prices", 'value': 'all_variants'},
      {'label': 'If discount percentage is within range', 'value': 'percentage'}
    ]
    if (this.state.redirectToListing === true) {
      return <Redirect to='/collection-listing?tabindex=0' />
    }
    let saveAndSyncDisabled = this.state.collectionTitle.length === 0 || this.hasInvalidNewRules() ||
      (this.state.selectedSmartCollectionIds.length===0 && !this.state.includeAllProducts &&
      this.state.includeTitleRules.length===0 &&
      this.state.includeContainsTags.length===0 && this.state.includeStartsWithTags.length===0 && this.state.includeEndsWithTags.length===0 && this.state.includeTypes.length===0 && this.state.includeVendors.length===0 &&
      !this.state.includePriceLowerThanComparedAt && this.state.includeSelectedTags.length===0 && this.state.includeNewnessType === 'none' && this.state.includeInventoryType === 'none') ||
      (this.state.includeNewnessType !== 'none' && (this.state.includeNewnessDays === '' || this.state.includeNewnessDays === '0')) ||
      (this.state.excludeNewnessType !== 'none' && (this.state.excludeNewnessDays === '' || this.state.excludeNewnessDays === '0') ||
      (this.state.includeInventoryType !== 'none' && this.state.includeInventoryQuantity === '' ) ||
      (this.state.excludeInventoryType !== 'none' && this.state.excludeInventoryQuantity === '' )
    );
    return (
        <Page
          breadcrumbs={[{content: 'Manage Collections', onAction:()=>this.setState({redirectToListing: true})}]}>
          <DisplayText size="large">{this.props.type === 'new' ? "Create New Advanced Collection" : "Edit Collection"}</DisplayText>
          {this.state.showBanner && this.props.type === 'existing' && (<Banner
            title="Modifying an advanced collection"
            status="info"
            onDismiss={() => {this.setState({showBanner: false})}}>
            <p>After you save the modified collection, the app will save the changed collection title on Shopify. If auto-sync is enabled, the app will start syncing your products according to your newly chosen conditions.</p>
          </Banner>)}
          {this.state.showBanner && this.props.type === 'new' && (<Banner
            title="How this works"
            status="info"
            onDismiss={() => {this.setState({showBanner: false})}}>
            <p>After you create an advanced collection, the app will create a manual collection with the same title on Shopify and start syncing your products to this collection according to the conditions you have chosen.</p>
          </Banner>)}
          {!this.state.hasLoaded && (
            <div className="loader">
              <Layout><Spinner size="large" /></Layout>
              <Layout><DisplayText size="small">Loading tags / vendors / product types / collections selection lists... This might take a few minutes if you have many collections and products. Thank you for your patience.</DisplayText></Layout>
            </div>
          )}          
          {this.state.hasLoaded && (
            <div>
              <Card sectioned title="Advanced collection options">
              <div className="indent-section">
                <FormLayout>
                <TextField
                  label="Enter collection title (required)"
                  value={this.state.collectionTitle}
                  onChange={this.handleTitleChange}
                  error={this.state.collectionTitleTouched && this.state.collectionTitle.length==0 ? "Collection name is required" : 
                         //!collectionTitleRegex.test(this.state.collectionTitle) ? "Collection name contains illegal characters" :
                         false}
                />
                <Checkbox
                  checked={this.state.autoSync}
                  label="Auto Sync"
                  onChange={this.handleAutoSyncChange}
                />
                <p>If auto syncing is enabled, the advanced collection will sync automatically every time you update or create a product which matches the conditions you have chosen.</p>
                </FormLayout>
                </div>
              </Card>
              <Layout>
                <Layout.Section oneHalf>
                <Card sectioned>
                  <FormLayout>
                    <div className="text-center"><img className="illustrate" src={collectionImage} /></div>
                    <p className="text-center"><strong>How to Choose Conditions</strong></p>
                    <p className="text-center">There are many conditions you can choose from to create advanced collections. While the list of options is long, do not fret! You probably just need a few, and they are easy to understand. To give an overview on how this works:</p>
                    <p className="text-center">
                      1. Products which satisfy <strong>any of the include conditions</strong> are first added.
                    </p>
                    <p className="text-center">
                      2. Next, products which satisfy <strong>any of the exclude conditions</strong> are removed. 
                    </p>
                    <p className="text-center">
                      3. Finally, only products<strong> which match all of the filter conditions</strong> remains on the list.
                    </p>
                  </FormLayout>
                </Card>
                </Layout.Section>
                <Layout.Section oneHalf>
                <Card sectioned>
                  <FormLayout>
                    <div className="text-center"><img className="illustrate" src={selectionListImage} /></div>
                    <p className="text-center"><strong>Refresh Selection Lists</strong></p>
                    <p className="text-center">Refresh the selection lists if you cannot find a tag, vendor, product type or collection. Manual and automated Shopify collections are available, including those managed by Advanced Collections. A collection cannot select itself.</p>
                    <p className="text-center"><Button primary onClick={this.refreshPage}>Refresh Selection Lists</Button></p>
                    <p className="text-center subdued">In order to speed up loading time, your store’s selection lists are cached.</p>
                    <p className="text-center subdued">If your store has a lot of collections and products, the refreshing time may take a few minutes.</p>
                  </FormLayout>
                </Card>
                </Layout.Section>
              </Layout>
              <Card sectioned title="Include products">
              <p style={{paddingBottom:'20px'}}>Choose the conditions by which products will be included in your advanced collection. Products matching <strong>ANY</strong> of these conditions will be included. At least 1 condition is needed here.</p>
              <div className="indent-section">
                <FormLayout>
                <TextStyle variation="strong">Include all products?</TextStyle>
                <Checkbox
                  checked={this.state.includeAllProducts}
                  label="Include all products"
                  onChange={this.handleIncludeAllProducts}
                />
                {this.state.includeAllProducts && invalidTitleRules(this.state.includeTitleRules) && (
                  <Banner status="critical">An included title condition needs attention. Turn off Include all products to edit or remove it before saving.</Banner>
                )}
                
                {!this.state.includeAllProducts && (
                  <FormLayout>
                    <hr className="minor-divider" style={{marginTop:'30px'}} />
                    <TitleRules label="Include title" title="Title includes" match="ANY"
                      rules={this.state.includeTitleRules} onChange={includeTitleRules => this.setState({includeTitleRules})} />
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Tags includes', 'Refresh Tags')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.includeSelectedTags} onChange={this.includeTagsChange} placeholderText='Type and press enter'>Include product if one of its tags <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Tags to include' selected={this.state.includeSelectedTags} onChange={this.includeTagsChange} options={this.state.availableTags} placeholderText='Type to start searching'>Include product if one of its tags <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                    )}
                    <TagsInput selected={this.state.includeStartsWithTags} onChange={this.handleIncludeStartsWithTagsChange} placeholderText='Type and press enter'>Include product if one of its tags <strong>STARTS</strong> with <strong>ANY</strong> of the following:</TagsInput>
                    <TagsInput selected={this.state.includeEndsWithTags} onChange={this.handleIncludeEndsWithTagsChange} placeholderText='Type and press enter'>Include product if one of its tags <strong>ENDS</strong> with <strong>ANY</strong> of the following:</TagsInput>
                    <TagsInput selected={this.state.includeContainsTags} onChange={this.handleIncludeContainsTagsChange} placeholderText='Type and press enter'>Include product if one of its tags <strong>CONTAINS</strong> <strong>ANY</strong> of the following:</TagsInput>
                    <hr className="minor-divider" />
                    <TextStyle variation="strong">Price includes</TextStyle>
                    <Checkbox
                      checked={this.state.includePriceLowerThanComparedAt}
                      label="Include products if 'Price' is lower than 'Compared At Price'"
                      onChange={this.handleIncludePriceLowerThanComparedAtChange}
                    />
                    {this.state.includePriceLowerThanComparedAt && (
                      <Select
                        label="Criteria"
                        options={discountTypeOptions}
                        onChange={this.handleIncludeDiscountTypeChange}
                        value={this.state.includeDiscountType}
                      />
                    )}
                    {this.state.includePriceLowerThanComparedAt && this.state.includeDiscountType === 'percentage' &&(
                      <RangeSlider
                        output
                        label="Include products if discount percentage is within the following range"
                        value={this.state.includeDiscountPercentageRange}
                        suffix='%'
                        min={0}
                        max={100}
                        step={1}
                        onChange={this.handleIncludeDiscountPercentageRangeChange}
                      />
                    )}
                    <hr className="minor-divider" />
                    <TextStyle variation="strong">Date includes</TextStyle>
                    <Select
                      label="Include products based on date:"
                      options={newnessTypeOptions}
                      onChange={this.handleIncludeNewnessTypeChange}
                      value={this.state.includeNewnessType}
                    />
                    {this.state.includeNewnessType !== 'none' && (
                      <TextField
                        label="Number of days"
                        type="number"
                        value={this.state.includeNewnessDays}
                        onChange={this.handleIncludeNewnessDaysChange}
                        error={(this.state.includeNewnessDays === '' || this.state.includeNewnessDays === '0') && this.state.includeNewnessType !== 'none'}
                      />
                    )}
                    <hr className="minor-divider" />
                    <TextStyle variation="strong">Inventory includes</TextStyle>
                    <Select
                      label="Include products based on inventory quantities:"
                      options={inventoryTypeOptions}
                      onChange={this.handleIncludeInventoryTypeChange}
                      value={this.state.includeInventoryType}
                    />
                    {this.state.includeInventoryType !== 'none' && (
                      <TextField
                        label="Quantity"
                        type="number"
                        value={this.state.includeInventoryQuantity}
                        onChange={this.handleIncludeInventoryQuantityChange}
                        error={this.state.includeInventoryQuantity === '' && this.state.includeInventoryType !== 'none'}
                      />
                    )}
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Collections includes', 'Refresh Collections')}
                    <AutocompleteTags preserveUnavailableCollections requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText={collectionSelectionHelp} listTitle='Collection includes' selected={this.state.selectedSmartCollectionIds} onChange={this.selectedSmartCollectionChange} options={selectableCollections} placeholderText='Type to start searching'>Include products from the following collections:</AutocompleteTags>
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Vendor includes', 'Refresh Vendors')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.includeVendors} onChange={this.handleIncludeVendorsChange} placeholderText='Type and press enter'>Include product if one of its vendors <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Vendors to include' selected={this.state.includeVendors} onChange={this.handleIncludeVendorsChange} options={this.state.vendorOptions} placeholderText='Type to start searching'>Include product if one of its vendors <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                    )}
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Product Type includes', 'Refresh Product Types')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.includeTypes} onChange={this.handleIncludeTypesChange} placeholderText='Type and press enter'>Include product if one of its product types <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Product Types to include' selected={this.state.includeTypes} onChange={this.handleIncludeTypesChange} options={this.state.typeOptions} placeholderText='Type to start searching'>Include product if one of its product types <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                    )}
                  </FormLayout>
                )}
                </FormLayout>
              </div>
              </Card>

              <Card sectioned title="Exclude products">
                <p style={{paddingBottom:'20px'}}>Choose the conditions by which products will be excluded. Products matching <strong>ANY</strong> of these conditions will be removed from the above.</p>
                <div className="indent-section">
                <FormLayout>
                  <TitleRules label="Exclude title" title="Title excludes" match="ANY"
                    rules={this.state.excludeTitleRules} onChange={excludeTitleRules => this.setState({excludeTitleRules})} />
                  <hr className="minor-divider" />
                  {this.renderSelectionHeading('Tags excludes', 'Refresh Tags')}
                  {this.state.exceedProductsCount ? (
                    <TagsInput selected={this.state.excludeSelectedTags} onChange={this.excludeTagsChange} placeholderText='Type and press enter'>Exclude product if one of its tags <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                  ) : (
                    <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Tags to exclude' selected={this.state.excludeSelectedTags} onChange={this.excludeTagsChange} options={this.state.availableTags} placeholderText='Type to start searching'>Exclude product if one of its tags <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                  )}
                  <TagsInput selected={this.state.excludeStartsWithTags} onChange={this.handleExcludeStartsWithTagsChange} placeholderText='Type and press enter'>Exclude product if one of its tags <strong>STARTS</strong> with <strong>ANY</strong> of the following:</TagsInput>
                  <TagsInput selected={this.state.excludeEndsWithTags} onChange={this.handleExcludeEndsWithTagsChange} placeholderText='Type and press enter'>Exclude product if one of its tags <strong>ENDS</strong> with <strong>ANY</strong> of the following:</TagsInput>
                  <TagsInput selected={this.state.excludeContainsTags} onChange={this.handleExcludeContainsTagsChange} placeholderText='Type and press enter'>Exclude product if one of its tags <strong>CONTAINS</strong> <strong>ANY</strong> of the following:</TagsInput>
                  <hr className="minor-divider" />
                  <TextStyle variation="strong">Price excludes</TextStyle>
                  <Checkbox
                    checked={this.state.excludePriceLowerThanComparedAt}
                    label="Exclude products if 'Price' is lower than 'Compared at Price'"
                    onChange={this.handleExcludePriceLowerThanComparedAtChange}
                  />
                  {this.state.excludePriceLowerThanComparedAt && (
                    <Select
                        label="Criteria"
                        options={discountTypeOptions}
                        onChange={this.handleExcludeDiscountTypeChange}
                        value={this.state.excludeDiscountType}
                      />
                  )}
                  {this.state.excludePriceLowerThanComparedAt && this.state.excludeDiscountType === 'percentage' &&(
                      <RangeSlider
                        output
                        label="Exclude products if discount percentage is within the following range"
                        value={this.state.excludeDiscountPercentageRange}
                        suffix='%'
                        min={0}
                        max={100}
                        step={1}
                        onChange={this.handleExcludeDiscountPercentageRangeChange}
                      />
                  )}
                  <hr className="minor-divider" />
                  <TextStyle variation="strong">Date excludes</TextStyle>
                  <Select
                      label="Exclude products based on date:"
                      options={newnessTypeOptions}
                      onChange={this.handleExcludeNewnessTypeChange}
                      value={this.state.excludeNewnessType}
                    />
                    {this.state.excludeNewnessType !== 'none' && (
                      <TextField
                        label="Number of days"
                        type="number"
                        value={this.state.excludeNewnessDays}
                        onChange={this.handleExcludeNewnessDaysChange}
                        error={(this.state.excludeNewnessDays === '' || this.state.excludeNewnessDays === '0') && this.state.excludeNewnessType !== 'none'}
                      />
                    )}
                    <hr className="minor-divider" />
                    <TextStyle variation="strong">Inventory excludes</TextStyle>
                    <Select
                        label="Exclude products based on inventory quantities:"
                        options={inventoryTypeOptions}
                        onChange={this.handleExcludeInventoryTypeChange}
                        value={this.state.excludeInventoryType}
                    />
                    {this.state.excludeInventoryType !== 'none' && (
                      <div>
                        <TextField
                          label="Quantity"
                          type="number"
                          value={this.state.excludeInventoryQuantity}
                          onChange={this.handleExcludeInventoryQuantityChange}
                          error={this.state.excludeInventoryQuantity === '' && this.state.excludeInventoryType !== 'none'}
                        />
                        <Checkbox
                          label="Do not exclude product if any of its variants has policy to continue selling even when out of stock"
                          checked={this.state.excludeInventoryIgnoreContinue}
                          onChange={this.handleExcludeInventoryIgnoreContinueChange}
                        />
                        <Checkbox
                          label="Do not exclude product if any of its variants' inventory is untracked"
                          checked={this.state.excludeInventoryIgnoreUntracked}
                          onChange={this.handleExcludeInventoryIgnoreUntrackedChange}
                        />
                      </div>
                    )}

                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Collections excludes', 'Refresh Collections')}
                    <AutocompleteTags preserveUnavailableCollections requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText={collectionSelectionHelp} listTitle='Collection excludes' selected={this.state.excludedSmartCollectionIds} onChange={this.excludedSmartCollectionChange} options={selectableCollections} placeholderText='Type to start searching'>Exclude products from the following collections:</AutocompleteTags>

                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Vendor excludes', 'Refresh Vendors')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.excludeVendors} onChange={this.handleExcludeVendorsChange} placeholderText='Type and press enter'>Exclude product if one of its vendors <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Vendors to include' selected={this.state.excludeVendors} onChange={this.handleExcludeVendorsChange} options={this.state.vendorOptions} placeholderText='Type to start searching'>Exclude product if one of its vendors <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                    )}
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Product Type excludes', 'Refresh Product Types')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.excludeTypes} onChange={this.handleExcludeTypesChange} placeholderText='Type and press enter'>Exclude product if one of its product types <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Product Types to exclude' selected={this.state.excludeTypes} onChange={this.handleExcludeTypesChange} options={this.state.typeOptions} placeholderText='Type to start searching'>Exclude product if one of its product types <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                    )}
                </FormLayout>
                </div>
              </Card>

              <Card sectioned title="Filter products">
                <p style={{paddingBottom:'20px'}}>Choose the conditions by which the products above will be filtered. Only products which matches <strong>ALL</strong> of the following conditions will remain. Filter conditions are not required.</p>
                <div className="indent-section">
                  <FormLayout>
                    <TitleRules label="Filter title" title="Title filter" match="ALL"
                      rules={this.state.filterTitleRules} onChange={filterTitleRules => this.setState({filterTitleRules})} />
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Collections filter', 'Refresh Collections')}
                    <AutocompleteTags preserveUnavailableCollections requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText={collectionSelectionHelp} listTitle='Collections to filter' selected={this.state.filteredSmartCollectionIds} onChange={this.filteredSmartCollectionChange} options={selectableCollections} placeholderText='Type to start searching'>Keep products only if they belong to <strong>EVERY</strong> selected collection:</AutocompleteTags>
                    {this.state.filteredSmartCollectionIds.length > 100 && <Banner status="critical">Select no more than 100 collections to filter.</Banner>}
                    <hr className="minor-divider" />
                    {this.renderSelectionHeading('Tag filter', 'Refresh Tags')}
                    {this.state.exceedProductsCount ? (
                      <TagsInput selected={this.state.filterSelectedTags} onChange={this.filterTagsChange} placeholderText='Type and press enter'>Include product only if its tags <strong>MATCHES ALL</strong> of the following:</TagsInput>
                    ) : (
                      <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Tags to filter' selected={this.state.filterSelectedTags} onChange={this.filterTagsChange} options={this.state.availableTags} placeholderText='Type to start searching'>Include product only if its tags <strong>MATCHES ALL</strong> of the following:</AutocompleteTags>
                    )}
                    <hr className="minor-divider" />
                    <TextStyle variation="strong">Price filter</TextStyle>
                    <Checkbox
                    checked={this.state.filterPriceLowerThanComparedAt}
                    label="Include products only if 'Price' is lower than 'Compared at Price'"
                    onChange={this.handleFilterPriceLowerThanComparedAtChange}
                  />
                  {this.state.filterPriceLowerThanComparedAt && (
                    <Select
                        label="Criteria"
                        options={discountTypeOptions}
                        onChange={this.handleFilterDiscountTypeChange}
                        value={this.state.filterDiscountType}
                      />
                  )}
                  {this.state.filterPriceLowerThanComparedAt && this.state.filterDiscountType === 'percentage' &&(
                      <RangeSlider
                        output
                        label="Include products only if discount percentage is within the following range"
                        value={this.state.filterDiscountPercentageRange}
                        suffix='%'
                        min={0}
                        max={100}
                        step={1}
                        onChange={this.handleFilterDiscountPercentageRangeChange}
                      />
                  )}
                   <hr className="minor-divider" />
                  {this.renderSelectionHeading('Vendor filter', 'Refresh Vendors')}
                  {this.state.exceedProductsCount ? (
                    <TagsInput selected={this.state.filterVendors} onChange={this.handleFilterVendorsChange} placeholderText='Type and press enter'>Include products only if its vendor <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                  ) : (
                    <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Vendors to filter' selected={this.state.filterVendors} onChange={this.handleFilterVendorsChange} options={this.state.vendorOptions} placeholderText='Type to start searching'>Include products only if its vendor <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                  )}
                  <hr className="minor-divider" />
                  {this.renderSelectionHeading('Product Type filter', 'Refresh Product Types')}
                  {this.state.exceedProductsCount ? (
                    <TagsInput selected={this.state.filterTypes} onChange={this.handleFilterTypesChange} placeholderText='Type and press enter'>Include products only if its product type <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</TagsInput>
                  ) : (
                    <AutocompleteTags requiresTypingToFilter={true} errorLoading={this.state.errorLoading} helpText="A list to select will appear after you type 2 or more letters. Type more to filter more if it is hard to select / deselect" listTitle='Product Types to filter' selected={this.state.filterTypes} onChange={this.handleFilterTypesChange} options={this.state.typeOptions} placeholderText='Type to start searching'>Include products only if its product type <strong>EQUALS</strong> to <strong>ANY</strong> of the following:</AutocompleteTags>
                  )}
                  </FormLayout>
                </div>
                </Card>
                <Card sectioned>
                  {this.state.saveError && <Banner status="critical" title="Collection not saved">{this.state.saveError}</Banner>}
                  <Stack distribution="trailing">
                  <Button onClick ={()=>this.setState({redirectToListing: true})} disabled={this.state.isSaving}>Cancel</Button>
                  <Button primary onClick={this.save} loading={this.state.isSaving} disabled={saveAndSyncDisabled}>{this.props.type==='new' || this.state.autoSync ? 'Save & Sync' : 'Save without syncing'}</Button>
                  </Stack>
                </Card>
              </div>
          )}
        </Page>
    );
  }
}

export default Collection;
